import * as React from 'react'
import { Alert, Button, Modal } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faRotate } from '@fortawesome/pro-duotone-svg-icons'
import { useSecureShare } from './useSecureShare'
import { StepOne } from './StepOne'
import { StepTwo } from './StepTwo'
import { StepThree } from './StepThree'
import { useCallback } from 'react'
import { toast } from 'react-toastify'

type Props = {
    kedo: any
    contentDossier: any
    closeModal: () => void
}

const ContentDossierSecureShare: React.FC<Props> = ({
    kedo,
    contentDossier,
    closeModal,
}) => {
    const { state, setPartialState } = useSecureShare({ kedo, contentDossier })

    const nextStep = () => setPartialState({ step: state.step + 1 })
    const prevStep = () => setPartialState({ step: state.step - 1 })

    const renderTitle = () => {
        const base = kedo.t('Secure share data', { ns: 'secureshare' })
        const steps = ['Recipients', 'Configuration', 'Summary']
        return `${base}: ${kedo.t(steps[state.step - 1])}`
    }

    const transformContactsForPayload = useCallback(
        async (
            recipientUsers: any[],
            dossierPersons: any[],
            customEmails: any[]
        ) => {
            const contacts: any[] = []

            for (const user of recipientUsers) {
                const res = await kedo
                    .api()
                    .get(kedo.api().getUserEndpoint(user.id))
                contacts.push({
                    email: res.data.email,
                    mobile: res.data.mobile,
                })
            }

            for (const email of customEmails || []) {
                contacts.push({ email: email.value })
            }

            for (const person of dossierPersons || []) {
                contacts.push({ email: person.email, mobile: person.mobile })
            }

            setPartialState({ contacts })
        },
        [kedo, setPartialState]
    )

    const renderCurrentStep = () => {
        switch (state.step) {
            case 1:
                return (
                    <StepOne
                        kedo={kedo}
                        state={state}
                        setPartialState={setPartialState}
                        transformContactsForPayload={
                            transformContactsForPayload
                        }
                    />
                )
            case 2:
                return (
                    <StepTwo
                        kedo={kedo}
                        state={state}
                        setPartialState={setPartialState}
                    />
                )
            case 3:
                return <StepThree kedo={kedo} state={state} />
            default:
                return null
        }
    }

    const handleSend = async () => {
        setPartialState({ processing: true, error: false })
        const secureShareUrl = kedo
            .api()
            .getSecureShareEndpoint(contentDossier.id)

        const payload = {
            activeUntil: state.shareUntilDate,
            contacts: state.contacts,
            settings: {
                share_type: state.shareType,
                share_value: state.shareTypeValue,
            },
        }

        try {
            await kedo.api().post(secureShareUrl, payload)
            toast.success(kedo.t('Information secure shared'), {
                position: 'bottom-center',
                autoClose: 2500,
            })
            setPartialState({ processing: false, error: false })
            closeModal()
        } catch {
            setPartialState({ processing: false, error: true })
        }
    }

    return (
        <Modal
            show
            onHide={closeModal}
            size="xl"
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton>
                <Modal.Title>{renderTitle()}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {state.error && (
                    <Alert variant="danger">
                        {kedo.t('Something went wrong')}
                    </Alert>
                )}
                {renderCurrentStep()}
            </Modal.Body>
            <Modal.Footer>
                <Button disabled={state.processing} onClick={closeModal}>
                    {kedo.t('Cancel')}
                </Button>
                {state.step > 1 && (
                    <Button variant="secondary" onClick={prevStep}>
                        {kedo.t('Previous')}
                    </Button>
                )}
                {state.step <= 2 && (
                    <Button
                        variant="secondary"
                        onClick={nextStep}
                        disabled={
                            state.processing ||
                            (!state.contacts.length &&
                                !state.recipientUsers.length)
                        }
                    >
                        {kedo.t('Next')}
                    </Button>
                )}
                {state.step === 3 && (
                    <Button onClick={handleSend} disabled={state.processing}>
                        <span className="mr-2">{kedo.t('Send')}</span>
                        <FontAwesomeIcon
                            icon={state.processing ? faRotate : faEnvelope}
                            spin={state.processing}
                        />
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    )
}

export default ContentDossierSecureShare
